"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiSetPinnedDropRequest } from "@/generated/models/ApiSetPinnedDropRequest";
import type { ApiWave } from "@/generated/models/ApiWave";
import { commonApiPost } from "@/services/api/common-api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
interface SetWavePinnedDropInput {
  readonly dropId: string;
}
interface UseSetWavePinnedDropOptions {
  readonly onSuccess?: ((wave: ApiWave, input: SetWavePinnedDropInput) => void) | undefined;
  readonly onError?: ((message: string, error: unknown, input: SetWavePinnedDropInput) => void) | undefined;
}
interface UseSetWavePinnedDropResult {
  readonly setPinnedDrop: (input: SetWavePinnedDropInput) => Promise<ApiWave>;
  readonly data: ApiWave | undefined;
  readonly isPending: boolean;
  readonly pendingDropId: string | null;
  readonly error: unknown;
  readonly errorMessage: string | null;
  readonly reset: () => void;
}
const toErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Something went wrong";
};
export function useSetWavePinnedDrop(waveId, options) {
  const $ = _c(25);
  const queryClient = useQueryClient();
  const [pendingDropId, setPendingDropId] = useState(null);
  let t0;
  if ($[0] !== waveId) {
    t0 = async t1 => {
      const {
        dropId
      } = t1;
      return await commonApiPost({
        endpoint: `waves/${waveId}/pinned-drop`,
        body: {
          drop_id: dropId
        }
      });
    };
    $[0] = waveId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== queryClient || $[4] !== waveId) {
    t1 = async (wave, input) => {
      queryClient.setQueryData([QueryKey.WAVE, {
        wave_id: waveId
      }], wave);
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.DROPS],
        predicate: query => {
          const [, params] = query.queryKey;
          return typeof params === "object" && params !== null && Reflect.get(params, "waveId") === waveId;
        }
      });
      options?.onSuccess?.(wave, input);
    };
    $[2] = options;
    $[3] = queryClient;
    $[4] = waveId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== options) {
    t2 = (error, input_0) => {
      options?.onError?.(toErrorMessage(error), error, input_0);
    };
    $[6] = options;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setPendingDropId(null);
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t4 = {
      mutationFn: t0,
      onSuccess: t1,
      onError: t2,
      onSettled: t3
    };
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const mutation = useMutation(t4);
  let t5;
  if ($[13] !== mutation) {
    t5 = async input_1 => {
      setPendingDropId(input_1.dropId);
      return await mutation.mutateAsync(input_1);
    };
    $[13] = mutation;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const setPinnedDrop = t5;
  let t6;
  bb0: {
    if (mutation.error === null) {
      t6 = null;
      break bb0;
    }
    let t7;
    if ($[15] !== mutation.error) {
      t7 = toErrorMessage(mutation.error);
      $[15] = mutation.error;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t6 = t7;
  }
  const errorMessage = t6;
  let t7;
  if ($[17] !== errorMessage || $[18] !== mutation.data || $[19] !== mutation.error || $[20] !== mutation.isPending || $[21] !== mutation.reset || $[22] !== pendingDropId || $[23] !== setPinnedDrop) {
    t7 = {
      setPinnedDrop,
      data: mutation.data,
      isPending: mutation.isPending,
      pendingDropId,
      error: mutation.error,
      errorMessage,
      reset: mutation.reset
    };
    $[17] = errorMessage;
    $[18] = mutation.data;
    $[19] = mutation.error;
    $[20] = mutation.isPending;
    $[21] = mutation.reset;
    $[22] = pendingDropId;
    $[23] = setPinnedDrop;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}